import { useState } from 'react';
import {
  Group,
  Text,
  Stack,
  Box,
  Avatar,
  Badge,
  Collapse,
  ActionIcon,
} from '@mantine/core';
import { ChevronDown, ChevronRight, MessageCircle, Check } from 'react-feather';
import type { CoachHuddle } from '../../../../types';
import { formatDate, formatTime } from '../../../../utils/formatters';
import { getAvatarColor } from '../../../../utils/avatarHelpers';

// MED-1350: intro and midpoint check-ins are 1:1 rows now and show in the 1:1
// section. This section lists general check-ins only.
interface CheckInCollapsibleSectionProps {
  huddles: CoachHuddle[];
  totalParticipants: number;
  onHuddleClick?: (huddleId: string) => void;
  onParticipantClick?: (participantId: string) => void;
  defaultExpanded?: boolean;
}

function computeStatus(huddle: CoachHuddle): 'completed' | 'scheduled' | 'unscheduled' {
  if (huddle.status === 'completed') return 'completed';
  if (huddle.status === 'scheduled' && huddle.scheduled_start) return 'scheduled';
  return 'unscheduled';
}

const STATUS_STYLES: Record<'completed' | 'scheduled' | 'unscheduled', { label: string; color: string }> = {
  completed: { label: 'Completed', color: 'medleyGreen' },
  scheduled: { label: 'Scheduled', color: 'blue' },
  unscheduled: { label: 'Unscheduled', color: 'gray' },
};

export function CheckInCollapsibleSection({
  huddles,
  totalParticipants,
  onHuddleClick,
  onParticipantClick,
  defaultExpanded = false,
}: CheckInCollapsibleSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const active = huddles.filter((h) => h.status !== 'cancelled');
  const completedCount = active.filter((h) => h.status === 'completed').length;
  const scheduledCount = active.filter(
    (h) => h.status === 'scheduled' && !!h.scheduled_start,
  ).length;
  const progressCount = completedCount + scheduledCount;
  const denominator = Math.max(totalParticipants, 0);
  const allCompleted = denominator > 0 && completedCount === denominator;
  const noneScheduled = progressCount === 0;

  let summaryText: string;
  let summaryColor: string;
  if (allCompleted) {
    summaryText = `${completedCount} of ${denominator} completed`;
    summaryColor = 'var(--mantine-color-medleyGreen-7)';
  } else if (noneScheduled) {
    summaryText = `0 of ${denominator} scheduled`;
    summaryColor = 'var(--mantine-color-gray-6)';
  } else {
    summaryText = `${progressCount} of ${denominator} scheduled`;
    summaryColor = 'var(--mantine-color-gray-7)';
  }

  const sortedForDisplay = [...active].sort((a, b) => {
    const aTime = a.scheduled_start ? new Date(a.scheduled_start).getTime() : Infinity;
    const bTime = b.scheduled_start ? new Date(b.scheduled_start).getTime() : Infinity;
    if (aTime !== bTime) return aTime - bTime;
    return (a.participant_name || '').localeCompare(b.participant_name || '');
  });

  return (
    <Box
      style={{
        backgroundColor: '#FAF9F6',
        borderBottom: '1px solid var(--mantine-color-gray-2)',
      }}
      data-testid="checkin-section-other"
    >
      <Group
        justify="space-between"
        wrap="nowrap"
        onClick={() => setExpanded((v) => !v)}
        style={{
          cursor: 'pointer',
          padding: '10px 20px',
          backgroundColor: '#FAF9F6',
        }}
      >
        <Group gap="sm" wrap="nowrap">
          <ActionIcon variant="subtle" color="gray" size="sm">
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </ActionIcon>
          <Box
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              border: '1px solid var(--mantine-color-gray-3)',
            }}
          >
            {allCompleted ? (
              <Check size={14} color="var(--mantine-color-medleyGreen-7)" />
            ) : (
              <MessageCircle size={14} color="#5C7A99" />
            )}
          </Box>
          <Text
            fw={700}
            size="11px"
            tt="uppercase"
            style={{ letterSpacing: '0.5px', color: '#2D4739' }}
          >
            OTHER / GENERAL CHECK-INS
          </Text>
          <Text
            size="12px"
            fw={500}
            style={{ color: summaryColor }}
          >
            {allCompleted ? '✓ ' : ''}
            {summaryText}
          </Text>
        </Group>
      </Group>

      <Collapse in={expanded}>
        {sortedForDisplay.length > 0 ? (
          <Stack gap={0}>
            {sortedForDisplay.map((huddle) => (
              <CheckInRow
                key={huddle.id}
                huddle={huddle}
                onHuddleClick={onHuddleClick}
                onParticipantClick={onParticipantClick}
              />
            ))}
          </Stack>
        ) : (
          <Text size="xs" c="dimmed" ta="center" py="sm">
            No general check-ins scheduled yet.
          </Text>
        )}
      </Collapse>
    </Box>
  );
}

interface CheckInRowProps {
  huddle: CoachHuddle;
  onHuddleClick?: (huddleId: string) => void;
  onParticipantClick?: (participantId: string) => void;
}

function CheckInRow({ huddle, onHuddleClick, onParticipantClick }: CheckInRowProps) {
  const status = computeStatus(huddle);
  const statusStyle = STATUS_STYLES[status];
  const initials = (huddle.participant_name || '')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const participantAvatarColor = getAvatarColor(
    huddle.participant_user_id || huddle.participant_id,
  );

  const actionLabel = status === 'completed' ? 'View Notes' : 'View / Edit';
  const hasSchedule = !!huddle.scheduled_start;

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      onClick={() => onHuddleClick?.(huddle.id)}
      style={{
        backgroundColor: '#FFFFFF',
        padding: '8px 20px 8px 56px',
        borderTop: '1px solid var(--mantine-color-gray-2)',
        cursor: onHuddleClick ? 'pointer' : 'default',
      }}
    >
      <Group gap="sm" wrap="nowrap">
        <Group
          gap="sm"
          wrap="nowrap"
          style={{
            cursor:
              onParticipantClick && huddle.cohort_participant_id ? 'pointer' : undefined,
          }}
          onClick={(e) => {
            if (onParticipantClick && huddle.cohort_participant_id) {
              e.stopPropagation();
              onParticipantClick(huddle.cohort_participant_id);
            }
          }}
        >
          <Avatar
            size={28}
            radius="xl"
            styles={{
              root: { backgroundColor: participantAvatarColor },
              placeholder: { color: '#FFFFFF', backgroundColor: participantAvatarColor },
            }}
          >
            {initials}
          </Avatar>
          <div>
            <Text fw={500} size="13px">
              {huddle.participant_name}
            </Text>
            {huddle.participant_title && (
              <Text size="10px" c="dimmed">
                {huddle.participant_title}
              </Text>
            )}
          </div>
        </Group>
      </Group>

      <Group gap="md" wrap="nowrap">
        <div style={{ textAlign: 'right', minWidth: 120 }}>
          {hasSchedule ? (
            <>
              <Text size="13px" fw={500}>
                {formatDate(huddle.scheduled_start)}
              </Text>
              <Text size="10px" c="dimmed">
                {formatTime(huddle.scheduled_start)}
                {huddle.duration_minutes
                  ? ` · ${huddle.duration_minutes} min`
                  : ''}
              </Text>
            </>
          ) : (
            <Text size="12px" c="dimmed" fs="italic">
              Not scheduled
            </Text>
          )}
        </div>
        <Badge
          size="sm"
          variant="light"
          color={statusStyle.color}
          tt="uppercase"
          style={{ letterSpacing: '0.3px', minWidth: 92, textAlign: 'center' }}
        >
          {statusStyle.label}
        </Badge>
        <Text
          size="12px"
          fw={600}
          c="var(--mantine-color-medleyGreen-7, #2D4739)"
          style={{ minWidth: 72, textAlign: 'right' }}
        >
          {actionLabel}
        </Text>
      </Group>
    </Group>
  );
}
